import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  uploadApi,
  tradesApi,
  coursesApi,
  communityApi,
  streamApi,
  adminApi,
  enrollmentApi,
  membershipApi,
  couponApi,
  failedPaymentsApi,
  newMembersApi
} from '../../services/api';
import chatWebSocket, { chatApi, playNotificationSound, initAudioContext } from '../../services/chatWebSocket';

const DashboardContext = createContext(null);

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within DashboardProvider');
  }
  return context;
};

export const DashboardProvider = ({ children }) => {
  const { user, logout, updateUser } = useAuth();
  const [searchParams] = useSearchParams();

  // Helper function to format date as MM/DD/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Handle date-only strings (YYYY-MM-DD) by parsing them as local dates, not UTC
    // This prevents timezone issues where dates appear a day off
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      return `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`;
    }
    // For full ISO timestamps, use toLocaleDateString for proper timezone handling
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  // Helper function to format message time in user's local timezone
  const formatMessageTime = (timestamp, fallbackTime) => {
    if (timestamp) {
      try {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          // Use user's local timezone for display
          return date.toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
        }
      } catch (e) {
        console.error('Error parsing timestamp:', e);
      }
    }
    return fallbackTime || '';
  };

  // UI State - persist active page across refreshes
  const [activePage, setActivePageState] = useState(() => {
    const savedPage = localStorage.getItem('hyperDriveActivePage');
    return savedPage || 'Dashboard';
  });
  
  // Wrapper to persist page changes
  const setActivePage = (page) => {
    setActivePageState(page);
    localStorage.setItem('hyperDriveActivePage', page);
  };
  
  const [countdown, setCountdown] = useState('20h 15m 37s');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');

  // Data State
  const [trades, setTrades] = useState([]);
  const [customPairs, setCustomPairs] = useState([]);
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersWithSubs, setUsersWithSubs] = useState([]);
  const [coupons, setCoupons] = useState([]);

  // Stream State
  const [streamData, setStreamData] = useState({
    isLive: false,
    zoomLink: 'https://us02web.zoom.us/j/8434583301',
    sessionDescription: "Today's focus: NAS and XAU analysis...",
    isTodayScheduled: true,
    cancellationMessage: "Today's trading session has been cancelled. See you next session!"
  });
  const [cancelledDates, setCancelledDates] = useState([]); // For countdown calculation
  const [nextSessionDate, setNextSessionDate] = useState(null); // Display next session info

  // Chat State
  const [activeChannel, setActiveChannel] = useState(''); // Will be set to first channel on load
  const [channelMessages, setChannelMessages] = useState({});
  const [newMessage, setNewMessage] = useState('');
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [chatImageUploading, setChatImageUploading] = useState(false);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');

  // Modal State
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [showAddPairModal, setShowAddPairModal] = useState(false);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [showChannelsModal, setShowChannelsModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showCoursesModal, setShowCoursesModal] = useState(false);
  const [showCourseBuilder, setShowCourseBuilder] = useState(false);
  const [showCourseViewer, setShowCourseViewer] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(null);

  // Edit State
  const [editingTrade, setEditingTrade] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingMembership, setEditingMembership] = useState(null);
  const [editingChannel, setEditingChannel] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingCourseId, setViewingCourseId] = useState(null);
  const [selectedUserForSubs, setSelectedUserForSubs] = useState(null);

  // Form State
  const [profileName, setProfileName] = useState(user?.name || '');
  const [tradingViewUsername, setTradingViewUsername] = useState(user?.trading_view_username || user?.tradingViewUsername || '');
  const [newPairName, setNewPairName] = useState('');
  const [enrollingCourseId, setEnrollingCourseId] = useState(null);
  const [upgradingMembershipId, setUpgradingMembershipId] = useState(null);
  const [redirectingToStripe, setRedirectingToStripe] = useState(false);

  // Helper to get local date string (YYYY-MM-DD)
  const getLocalDateString = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [tradeForm, setTradeForm] = useState({
    pair: '',
    direction: 'Long',
    session: 'New York',
    time: '',  // Trade time e.g., "9:43 AM"
    outcome: 'Win',
    entry: '',
    stopLoss: '',
    takeProfit: '',
    lotSize: '',
    pipsPoints: '',
    profitLoss: '',
    notes: '',
    date: getLocalDateString()  // Default to today's date in local timezone
  });

  // Failed payments state
  const [failedPayments, setFailedPayments] = useState([]);
  const [failedPaymentsCount, setFailedPaymentsCount] = useState(0);

  // New members state
  const [newMembers, setNewMembers] = useState([]);
  const [newMembersCount, setNewMembersCount] = useState(0);

  // Refs
  const fileInputRef = useRef(null);
  const chatInputRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const chatImageInputRef = useRef(null);

  const defaultPairs = ['NQ', 'MNQ', 'XAUUSD'];
  const commonEmojis = ['👍', '❤️', '😂', '😮', '😢', '🎉'];

  // Helper function to get Eastern Time offset (accounts for DST)
  const getEasternOffset = () => {
    // Use Intl API to properly detect if Eastern Time is in DST
    // This works regardless of the local timezone
    const now = new Date();
    
    // Get the timezone abbreviation for Eastern Time
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(now);
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    const tzAbbr = tzPart ? tzPart.value : 'EST';
    
    // EDT = Daylight Time (UTC-4), EST = Standard Time (UTC-5)
    return tzAbbr === 'EDT' ? -4 * 60 : -5 * 60;
  };

  // Helper function to get next 9:20 AM Eastern time session
  const getNextSessionTime = (cancelledDatesList = []) => {
    const now = new Date();
    const easternOffset = getEasternOffset();
    const localOffset = now.getTimezoneOffset();
    const diffMinutes = localOffset + easternOffset;
    const target = new Date(now);
    target.setHours(9, 20, 0, 0);
    target.setMinutes(target.getMinutes() - diffMinutes);
    
    // If current time is past today's session time, start from tomorrow
    if (now >= target) {
      target.setDate(target.getDate() + 1);
    }
    
    // Skip weekends and cancelled dates (check up to 30 days ahead)
    let attempts = 0;
    while (attempts < 30) {
      const dayOfWeek = target.getDay();
      const dateStr = target.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      // Skip weekends
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        target.setDate(target.getDate() + 1);
        attempts++;
        continue;
      }
      
      // Skip cancelled dates
      if (cancelledDatesList.includes(dateStr)) {
        target.setDate(target.getDate() + 1);
        attempts++;
        continue;
      }
      
      // Found a valid session date
      break;
    }
    
    return target;
  };

  // Helper function to format today's date for Stream Manager
  const getTodayFormatted = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const now = new Date();
    return `${days[now.getDay()]}, ${months[now.getMonth()]} ${now.getDate()}`;
  };

  // Countdown timer - accounts for cancelled sessions
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const target = getNextSessionTime(cancelledDates);
      const diff = target - now;
      
      // Update next session date for display
      setNextSessionDate(target);
      
      if (diff > 0) {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setCountdown(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setCountdown('Starting now!');
      }
    };
    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [cancelledDates]);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const tradesData = await tradesApi.getAll();
        setTrades(tradesData.map(t => ({
          ...t,
          profitLoss: t.profit_loss,
          stopLoss: t.stop_loss,
          takeProfit: t.take_profit,
          lotSize: t.lot_size,
          pipsPoints: t.pips_points,
          time: t.time || ''
        })));
      } catch (error) {
        console.log('Loading trades from localStorage fallback');
        const savedTrades = localStorage.getItem('hyperDriveTrades');
        if (savedTrades) setTrades(JSON.parse(savedTrades));
      }

      // Load custom pairs from backend (user-specific)
      try {
        const pairsData = await tradesApi.getCustomPairs();
        setCustomPairs(pairsData);
      } catch (error) {
        console.log('Failed to load custom pairs from server:', error);
        // Fallback to localStorage for backwards compatibility
        const savedPairs = localStorage.getItem('hyperDriveCustomPairs');
        if (savedPairs) setCustomPairs(JSON.parse(savedPairs));
      }

      try {
        // In dashboard, user is always logged in - try to get all memberships (including hidden)
        // This ensures admins always see all memberships in the admin panel
        let membershipsData;
        try {
          membershipsData = await communityApi.getAllMemberships();
        } catch (e) {
          // Fall back to public memberships if getAllMemberships fails (non-admin)
          membershipsData = await communityApi.getMemberships();
        }
        setMemberships(membershipsData);
      } catch (error) {
        console.log('Loading memberships from localStorage fallback');
        const savedMemberships = localStorage.getItem('hyperDriveMemberships');
        if (savedMemberships) setMemberships(JSON.parse(savedMemberships));
      }

      try {
        const channelsData = await communityApi.getChannels();
        setChannels(channelsData);
        
        // Set active channel to first visible channel if current channel doesn't exist
        const visibleChannels = channelsData.filter(c => c.visible !== false);
        if (visibleChannels.length > 0) {
          const channelNames = visibleChannels.map(c => c.name);
          setActiveChannel(prev => {
            // If current active channel doesn't exist in the list, switch to first one
            if (!channelNames.includes(prev)) {
              return visibleChannels[0].name;
            }
            return prev;
          });
        }
        
        const messagesObj = {};
        for (const channel of channelsData) {
          try {
            const messages = await communityApi.getMessages(channel.name);
            messagesObj[channel.name] = messages;
          } catch (e) {
            messagesObj[channel.name] = [];
          }
        }
        setChannelMessages(messagesObj);
      } catch (error) {
        console.log('Loading channels from localStorage fallback');
        const savedChannels = localStorage.getItem('hyperDriveChannels');
        if (savedChannels) {
          const parsedChannels = JSON.parse(savedChannels);
          setChannels(parsedChannels);
          // Also set active channel from fallback
          const visibleChannels = parsedChannels.filter(c => c.visible !== false);
          if (visibleChannels.length > 0) {
            setActiveChannel(prev => {
              if (!visibleChannels.map(c => c.name).includes(prev)) {
                return visibleChannels[0].name;
              }
              return prev;
            });
          }
        }
        const savedChannelMessages = localStorage.getItem('hyperDriveChannelMessages');
        if (savedChannelMessages) setChannelMessages(JSON.parse(savedChannelMessages));
      }

      try {
        const streamStatus = await streamApi.getStatus();
        setStreamData({
          isLive: streamStatus.is_live,
          zoomLink: streamStatus.zoom_link,
          sessionDescription: streamStatus.session_description,
          isTodayScheduled: streamStatus.is_today_scheduled,
          cancellationMessage: streamStatus.cancellation_message,
          cancelledSessions: streamStatus.cancelled_sessions
        });
      } catch (error) {
        console.log('Loading stream status from localStorage fallback');
        const savedStreamStatus = localStorage.getItem('streamLiveStatus');
        if (savedStreamStatus) {
          const status = JSON.parse(savedStreamStatus);
          setStreamData(prev => ({ ...prev, isLive: status.isLive }));
        }
      }

      try {
        const coursesData = await coursesApi.getAll();
        setCourses(coursesData.map(c => ({
          ...c,
          videoUrl: c.video_url,
          videoFileId: c.video_file_id,
          videoFileName: c.video_file_name,
          createdAt: c.created_at
        })));
      } catch (error) {
        console.log('Loading courses from localStorage fallback');
        const savedCourses = localStorage.getItem('hyperDriveCourses');
        if (savedCourses) setCourses(JSON.parse(savedCourses));
      }

      try {
        const usersData = await adminApi.getUsers();
        setUsers(usersData.map(u => ({
          ...u,
          joinDate: u.join_date,
          tradingViewUsername: u.trading_view_username
        })));
      } catch (error) {
        console.log('Loading users from localStorage fallback');
        const savedUsers = localStorage.getItem('hyperDriveUsers');
        if (savedUsers) setUsers(JSON.parse(savedUsers));
      }

      try {
        const enrollmentsData = await enrollmentApi.getMyEnrollments();
        setEnrollments(enrollmentsData);
      } catch (error) {
        console.log('Failed to load enrollments:', error);
      }

      // Load coupons (admin only)
      try {
        const couponsData = await couponApi.getAll();
        setCoupons(couponsData);
      } catch (error) {
        console.log('Coupons not loaded (may not be admin):', error);
      }

      // Load failed payments count (admin only)
      try {
        const countData = await failedPaymentsApi.getCount();
        setFailedPaymentsCount(countData.count || 0);
      } catch (error) {
        console.log('Failed payments count not loaded (may not be admin):', error);
      }

      // Load new members count (admin only)
      try {
        const countData = await newMembersApi.getCount();
        setNewMembersCount(countData.count || 0);
      } catch (error) {
        console.log('New members count not loaded (may not be admin):', error);
      }

      // Load user preferences for chat sound
      try {
        const token = localStorage.getItem('hyperdriveToken');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/preferences`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const prefs = await response.json();
          if (prefs.chat_sound !== undefined) {
            setSoundEnabled(prefs.chat_sound);
          }
        }
      } catch (error) {
        console.log('Failed to load preferences:', error);
      }
    };

    loadData();
  }, []);

  // Fetch online users periodically
  useEffect(() => {
    if (!user) return;
    
    const fetchOnlineUsers = async () => {
      try {
        // Send heartbeat to mark ourselves as online
        await communityApi.sendHeartbeat();
        
        // Get list of online users
        const data = await communityApi.getOnlineUsers();
        setOnlineUsers(data.users || []);
      } catch (error) {
        console.error('Failed to fetch online users:', error);
      }
    };
    
    // Fetch immediately
    fetchOnlineUsers();
    
    // Then fetch every 30 seconds
    const interval = setInterval(fetchOnlineUsers, 30 * 1000);
    
    return () => clearInterval(interval);
  }, [user]);

  // Real-time stream status polling
  // This ensures users see the "LIVE" indicator without refreshing
  useEffect(() => {
    if (!user) return;
    
    const checkStreamStatus = async () => {
      try {
        const streamStatus = await streamApi.getStatus();
        setStreamData(prev => {
          // Only update if status actually changed to avoid unnecessary re-renders
          if (prev.isLive !== streamStatus.is_live ||
              prev.zoomLink !== streamStatus.zoom_link ||
              prev.sessionDescription !== streamStatus.session_description ||
              prev.isTodayScheduled !== streamStatus.is_today_scheduled) {
            return {
              isLive: streamStatus.is_live,
              zoomLink: streamStatus.zoom_link,
              sessionDescription: streamStatus.session_description,
              isTodayScheduled: streamStatus.is_today_scheduled,
              cancellationMessage: streamStatus.cancellation_message,
              cancelledSessions: streamStatus.cancelled_sessions
            };
          }
          return prev;
        });
        
        // Also fetch schedule to get cancelled dates for countdown
        const scheduleData = await streamApi.getSchedule();
        if (scheduleData && scheduleData.schedule) {
          const cancelled = scheduleData.schedule
            .filter(day => day.status === 'cancelled')
            .map(day => day.date);
          setCancelledDates(prev => {
            // Only update if changed
            if (JSON.stringify(prev) !== JSON.stringify(cancelled)) {
              return cancelled;
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('Error checking stream status:', error);
      }
    };
    
    // Check immediately on mount
    checkStreamStatus();
    
    // Poll every 30 seconds for stream status (reduced from 10s for performance)
    const streamPollInterval = setInterval(checkStreamStatus, 30 * 1000);
    
    return () => clearInterval(streamPollInterval);
  }, [user]);

  // Site-wide notification polling (when NOT on Community page)
  // Uses polling instead of WebSocket to avoid conflicts
  useEffect(() => {
    if (!user || !channels.length) return;
    
    // Only poll if NOT on Community page (Community page uses WebSocket directly)
    if (activePage === 'Community') return;
    
    const mainChannel = channels.find(c => c.name === 'Community Chat') || channels[0];
    if (!mainChannel) return;
    
    let lastMessageId = null;
    
    const checkForNewMessages = async () => {
      try {
        const messages = await chatApi.getMessages(mainChannel.name);
        if (messages && messages.length > 0) {
          const latestMessage = messages[messages.length - 1];
          
          // If we have a new message from someone else
          if (lastMessageId && latestMessage.id !== lastMessageId && latestMessage.author_id !== user.id) {
            console.log('New message detected via polling:', latestMessage.author_name);
            
            // Update unread count
            setUnreadCounts(prev => ({
              ...prev,
              [mainChannel.name]: (prev[mainChannel.name] || 0) + 1
            }));
            
            // Play notification sound if enabled
            if (soundEnabled) {
              playNotificationSound();
            }
          }
          
          lastMessageId = latestMessage.id;
        }
      } catch (error) {
        console.error('Error checking for new messages:', error);
      }
    };
    
    // Check immediately to set lastMessageId
    checkForNewMessages();
    
    // Poll every 15 seconds for new messages (reduced from 5s for performance)
    const pollInterval = setInterval(checkForNewMessages, 15000);
    
    return () => clearInterval(pollInterval);
  }, [user, channels, activePage, soundEnabled]);

  // Handle payment callback
  useEffect(() => {
    const payment = searchParams.get('payment');
    const membershipPayment = searchParams.get('membership_payment');
    const sessionId = searchParams.get('session_id');

    if (payment === 'success' && sessionId) {
      const completeEnrollment = async () => {
        try {
          const result = await enrollmentApi.checkPaymentStatus(sessionId);
          if (result.status === 'complete' && result.payment_status === 'paid') {
            alert('Payment successful! You are now enrolled in the course.');
            const enrollmentsData = await enrollmentApi.getMyEnrollments();
            setEnrollments(enrollmentsData);
            setActivePage('Courses');
          }
        } catch (error) {
          console.error('Failed to verify payment:', error);
        }
      };
      completeEnrollment();
      window.history.replaceState({}, document.title, '/dashboard');
    } else if (payment === 'cancelled') {
      alert('Payment was cancelled. You can try again anytime.');
      window.history.replaceState({}, document.title, '/dashboard');
    }

    if (membershipPayment === 'success' && sessionId) {
      // Clear the URL params immediately to prevent double-processing
      window.history.replaceState({}, document.title, '/dashboard');
      
      const completeMembershipUpgrade = async () => {
        try {
          const result = await membershipApi.checkPaymentStatus(sessionId);
          // Accept 'paid', 'no_payment_required' (for 100% coupons), or 'complete' status
          if (result.status === 'complete' || result.payment_status === 'paid' || result.payment_status === 'no_payment_required') {
            alert(`Welcome! You are now a ${result.membership_name || 'premium'} member.`);
            // Reload to refresh user data
            window.location.reload();
          }
        } catch (error) {
          console.error('Failed to verify membership payment:', error);
          // Even if verification fails, reload to check if webhook processed it
          window.location.reload();
        }
      };
      completeMembershipUpgrade();
    } else if (membershipPayment === 'cancelled') {
      window.history.replaceState({}, document.title, '/dashboard');
      alert('Membership upgrade was cancelled. You can try again anytime.');
    }
  }, [searchParams]);

  // Calculate stats
  const calculateStats = useCallback(() => {
    if (trades.length === 0) {
      return {
        totalPL: 0,
        winRate: 0,
        wins: 0,
        losses: 0,
        avgRR: 0,
        totalTrades: 0,
        currentStreak: 0
      };
    }

    const totalPL = trades.reduce((sum, trade) => {
      const plValue = parseFloat(trade.profitLoss || 0);
      const adjustedPL = trade.outcome === 'Loss' ? -Math.abs(plValue) : Math.abs(plValue);
      return sum + adjustedPL;
    }, 0);
    const wins = trades.filter(t => t.outcome === 'Win').length;
    const losses = trades.filter(t => t.outcome === 'Loss').length;
    const totalTrades = wins + losses;
    const winRate = totalTrades > 0 ? ((wins / totalTrades) * 100).toFixed(1) : 0;

    let totalRR = 0;
    let rrCount = 0;
    trades.forEach(trade => {
      const entry = parseFloat(trade.entry || 0);
      const sl = parseFloat(trade.stopLoss || 0);
      const tp = parseFloat(trade.takeProfit || 0);
      if (entry && sl && tp) {
        const risk = Math.abs(entry - sl);
        const reward = Math.abs(tp - entry);
        if (risk > 0) {
          totalRR += reward / risk;
          rrCount++;
        }
      }
    });
    const avgRR = rrCount > 0 ? (totalRR / rrCount).toFixed(2) : 0;

    let currentStreak = 0;
    for (let i = trades.length - 1; i >= 0; i--) {
      if (trades[i].outcome === 'Win') {
        currentStreak++;
      } else if (trades[i].outcome === 'Loss') {
        break;
      }
    }

    return {
      totalPL: totalPL.toFixed(2),
      winRate,
      wins,
      losses,
      avgRR,
      totalTrades,
      currentStreak
    };
  }, [trades]);

  const stats = calculateStats();

  // Calculate equity curve data - MONTHLY (resets each month)
  const getEquityCurveData = useCallback(() => {
    if (trades.length === 0) return [];
    
    // Get current month trades only
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    
    const monthlyTrades = trades.filter(trade => {
      const tradeDate = trade.date?.split('T')[0];
      if (!tradeDate) return false;
      const [year, month] = tradeDate.split('-').map(Number);
      return year === currentYear && month === currentMonth;
    });
    
    if (monthlyTrades.length === 0) return [];
    
    let cumulativePL = 0;
    const sortedTrades = [...monthlyTrades].sort((a, b) => new Date(a.date) - new Date(b.date));
    return sortedTrades.map((trade, index) => {
      const plValue = parseFloat(trade.profitLoss) || 0;
      const adjustedPL = trade.outcome === 'Loss' ? -Math.abs(plValue) : Math.abs(plValue);
      cumulativePL += adjustedPL;
      return {
        index: index + 1,
        date: new Date(trade.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: cumulativePL
      };
    });
  }, [trades]);

  const equityCurveData = getEquityCurveData();

  // Trade handlers
  const handleTradeFormChange = (e) => {
    setTradeForm({
      ...tradeForm,
      [e.target.name]: e.target.value
    });
  };

  const handleAddTrade = async () => {
    const tradeData = {
      pair: tradeForm.pair,
      direction: tradeForm.direction,
      session: tradeForm.session,
      time: tradeForm.time,  // Trade time
      outcome: tradeForm.outcome,
      entry: tradeForm.entry,
      stop_loss: tradeForm.stopLoss,
      take_profit: tradeForm.takeProfit,
      lot_size: tradeForm.lotSize,
      pips_points: tradeForm.pipsPoints,
      profit_loss: tradeForm.profitLoss,
      notes: tradeForm.notes,
      date: tradeForm.date  // Use the selected date
    };

    try {
      if (editingTrade) {
        await tradesApi.update(editingTrade.id, tradeData);
      } else {
        await tradesApi.create(tradeData);
      }
      const updatedTrades = await tradesApi.getAll();
      setTrades(updatedTrades.map(t => ({
        ...t,
        profitLoss: t.profit_loss,
        stopLoss: t.stop_loss,
        takeProfit: t.take_profit,
        lotSize: t.lot_size,
        pipsPoints: t.pips_points,
        time: t.time || ''
      })));
    } catch (error) {
      console.error('Failed to save trade:', error);
      alert('Failed to save trade. Please try again.');
    }

    setShowTradeModal(false);    setEditingTrade(null);
    setTradeForm({
      pair: '',
      direction: 'Long',
      session: 'New York',
      time: '',
      outcome: 'Win',
      entry: '',
      stopLoss: '',
      takeProfit: '',
      lotSize: '',
      pipsPoints: '',
      profitLoss: '',
      notes: '',
      date: getLocalDateString()
    });
  };

  const handleEditTrade = (trade) => {
    setEditingTrade(trade);
    // Parse the trade date properly to avoid timezone issues
    let tradeDate = getLocalDateString();
    if (trade.date) {
      // If date is already in YYYY-MM-DD format, use it directly
      if (typeof trade.date === 'string' && trade.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        tradeDate = trade.date;
      } else {
        // Parse and convert to local date string
        const d = new Date(trade.date);
        tradeDate = getLocalDateString(d);
      }
    }
    setTradeForm({
      pair: trade.pair || '',
      direction: trade.direction || 'Long',
      session: trade.session || 'New York',
      time: trade.time || '',
      outcome: trade.outcome || 'Win',
      entry: trade.entry || '',
      stopLoss: trade.stopLoss || trade.stop_loss || '',
      takeProfit: trade.takeProfit || trade.take_profit || '',
      lotSize: trade.lotSize || trade.lot_size || '',
      pipsPoints: trade.pipsPoints || trade.pips_points || '',
      profitLoss: trade.profitLoss || trade.profit_loss || '',
      notes: trade.notes || '',
      date: tradeDate
    });
    setShowTradeModal(true);
  };

  const handleDeleteTrade = async (tradeId) => {
    try {
      await tradesApi.delete(tradeId);
      const updatedTrades = trades.filter(t => t.id !== tradeId);
      setTrades(updatedTrades);
    } catch (error) {
      console.error('Failed to delete trade:', error);
      alert('Failed to delete trade. Please try again.');
    }
  };

  // Silent refresh used by polling on the Performance page (and elsewhere)
  const refreshTrades = async () => {
    try {
      const updatedTrades = await tradesApi.getAll();
      setTrades(updatedTrades.map(t => ({
        ...t,
        profitLoss: t.profit_loss,
        stopLoss: t.stop_loss,
        takeProfit: t.take_profit,
        lotSize: t.lot_size,
        pipsPoints: t.pips_points,
        time: t.time || ''
      })));
    } catch (error) {
      console.warn('refreshTrades failed:', error);
    }
  };

  const handleAddCustomPair = async (pairNameOverride = null) => {
    const pairToAdd = pairNameOverride || newPairName.trim().toUpperCase();
    if (pairToAdd) {
      // Check if pair already exists locally
      if (!customPairs.includes(pairToAdd) && !defaultPairs.includes(pairToAdd)) {
        try {
          // Save to backend (user-specific)
          const result = await tradesApi.addCustomPair(pairToAdd);
          setCustomPairs(result.pairs || [...customPairs, pairToAdd]);
        } catch (error) {
          console.error('Failed to save custom pair to server:', error);
          // Fallback to local state update
          const updatedPairs = [...customPairs, pairToAdd];
          setCustomPairs(updatedPairs);
        }
      }
      setNewPairName('');
      setShowAddPairModal(false);
      return pairToAdd; // Return the added pair name for immediate use
    }
    return null;
  };

  // Enrollment handlers
  const handleEnrollCourse = async (courseId) => {
    setEnrollingCourseId(courseId);
    try {
      const result = await enrollmentApi.enrollInCourse(courseId);
      if (result.enrolled) {
        alert(result.message);
        const enrollmentsData = await enrollmentApi.getMyEnrollments();
        setEnrollments(enrollmentsData);
      } else if (result.checkout_url) {
        window.location.href = result.checkout_url;
      }
    } catch (error) {
      console.error('Enrollment failed:', error);
      alert('Failed to enroll. Please try again.');
    } finally {
      setEnrollingCourseId(null);
    }
  };

  const isEnrolled = (courseId) => {
    return enrollments.some(e => e.course_id === courseId);
  };

  const openCourseViewer = (courseId) => {
    setViewingCourseId(courseId);
    setShowCourseViewer(true);
  };

  // Stream handlers
  const handleGoLive = async () => {
    try {
      const result = await streamApi.toggleLive();
      console.log('Toggle live result:', result);
      setStreamData({ ...streamData, isLive: result.is_live });
      
      // If going live, refresh the Community Chat messages to show the announcement
      if (result.is_live) {
        // Small delay to ensure backend has posted the message
        setTimeout(async () => {
          try {
            const messages = await communityApi.getMessages('Community Chat');
            setChannelMessages(prev => ({
              ...prev,
              'Community Chat': messages
            }));
          } catch (e) {
            console.error('Failed to refresh chat messages:', e);
          }
        }, 500);
      }
    } catch (error) {
      console.error('Failed to toggle live:', error);
      const newLiveStatus = !streamData.isLive;
      setStreamData({ ...streamData, isLive: newLiveStatus });
    }
  };

  const handleCancelSession = async () => {
    try {
      const result = await streamApi.cancelSession(streamData.cancellationMessage);
      setStreamData(prev => ({ 
        ...prev, 
        isTodayScheduled: false,
        isLive: false
      }));
      return result;
    } catch (error) {
      console.error('Failed to cancel session:', error);
      throw error;
    }
  };

  const handleUncancelSession = async () => {
    try {
      const result = await streamApi.uncancelSession();
      setStreamData(prev => ({ 
        ...prev, 
        isTodayScheduled: true
      }));
      return result;
    } catch (error) {
      console.error('Failed to restore session:', error);
      throw error;
    }
  };

  // Membership handlers
  const handleSaveMembership = async () => {
    if (editingMembership) {
      try {
        const membershipData = {
          name: editingMembership.name,
          price: editingMembership.price,
          features: editingMembership.features,
          period: editingMembership.period || 'month',
          visible: editingMembership.visible !== false
        };
        if (editingMembership.isNew) {
          await communityApi.createMembership(membershipData);
        } else {
          await communityApi.updateMembership(editingMembership.id, membershipData);
        }
        // Use getAllMemberships to include hidden ones for admin view
        const updatedMemberships = await communityApi.getAllMemberships();
        setMemberships(updatedMemberships);
      } catch (error) {
        console.error('Failed to save membership:', error);
        alert('Failed to save membership. Please try again.');
      }
    }
    setEditingMembership(null);
  };

  const handleAddNewMembership = () => {
    const newMembership = {
      name: 'New Plan',
      price: 0,
      features: ['Feature 1', 'Feature 2'],
      period: 'month',
      visible: true,
      isNew: true
    };
    setEditingMembership(newMembership);
  };

  const handleDeleteMembership = async (id) => {
    try {
      await communityApi.deleteMembership(id);
      const updatedMemberships = memberships.filter(m => m.id !== id);
      setMemberships(updatedMemberships);
    } catch (error) {
      console.error('Failed to delete membership:', error);
      alert('Failed to delete membership. Please try again.');
    }
    setEditingMembership(null);
  };

  const handleUpgradeMembership = async (membership, couponCode = null) => {
    if (upgradingMembershipId) return;
    setUpgradingMembershipId(membership.id);
    try {
      const response = await membershipApi.upgradeMembership(membership.id, couponCode);
      if (response.checkout_url) {
        setRedirectingToStripe(true);
        setTimeout(() => {
          window.location.href = response.checkout_url;
        }, 500);
      } else if (response.success) {
        alert(response.message);
        window.location.reload();
      } else {
        alert(response.message || 'Unable to process upgrade');
        setUpgradingMembershipId(null);
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      alert(error.message || 'Failed to initiate upgrade. Please try again.');
      setUpgradingMembershipId(null);
    }
  };

  // State for checkout modal
  const [checkoutModalMembership, setCheckoutModalMembership] = useState(null);

  const openCheckoutModal = (membership) => {
    setCheckoutModalMembership(membership);
  };

  const closeCheckoutModal = () => {
    setCheckoutModalMembership(null);
    setUpgradingMembershipId(null);
  };

  const handleCheckoutWithCoupon = async (membership, couponCode) => {
    await handleUpgradeMembership(membership, couponCode);
    closeCheckoutModal();
  };

  const handleOpenBillingPortal = async () => {
    try {
      const response = await membershipApi.openBillingPortal();
      if (response.success && response.portal_url) {
        window.location.href = response.portal_url;
      } else {
        alert(response.message || 'Unable to open billing portal');
      }
    } catch (error) {
      console.error('Billing portal error:', error);
      alert('Failed to open billing portal. Please try again.');
    }
  };

  // Channel handlers
  const handleSaveChannel = async () => {
    if (editingChannel) {
      try {
        const channelData = {
          name: editingChannel.name,
          label: editingChannel.label
        };
        if (editingChannel.isNew) {
          const newChannel = await communityApi.createChannel(channelData);
          setChannels([...channels, newChannel]);
          setChannelMessages({
            ...channelMessages,
            [newChannel.name]: []
          });
        } else {
          const oldChannel = channels.find(c => c.id === editingChannel.id);
          await communityApi.updateChannel(editingChannel.id, channelData);
          const updatedChannels = channels.map(c =>
            c.id === editingChannel.id ? { ...c, ...channelData } : c
          );
          setChannels(updatedChannels);
          if (oldChannel && oldChannel.name !== editingChannel.name) {
            const updatedChannelMessages = { ...channelMessages };
            updatedChannelMessages[editingChannel.name] = channelMessages[oldChannel.name] || [];
            delete updatedChannelMessages[oldChannel.name];
            setChannelMessages(updatedChannelMessages);
            if (activeChannel === oldChannel.name) {
              setActiveChannel(editingChannel.name);
            }
          }
        }
      } catch (error) {
        console.error('Failed to save channel:', error);
        alert('Failed to save channel. Please try again.');
      }
    }
    setEditingChannel(null);
  };

  const handleAddNewChannel = () => {
    setEditingChannel({
      name: '',
      label: 'Community',
      visible: true,
      isNew: true
    });
  };

  const handleDeleteChannel = async (id) => {
    try {
      const channelToDelete = channels.find(c => c.id === id);
      await communityApi.deleteChannel(id);
      const updatedChannels = channels.filter(c => c.id !== id);
      setChannels(updatedChannels);
      if (channelToDelete && activeChannel === channelToDelete.name) {
        const firstChannel = updatedChannels[0];
        if (firstChannel) {
          setActiveChannel(firstChannel.name);
        }
      }
    } catch (error) {
      console.error('Failed to delete channel:', error);
      alert('Failed to delete channel. Please try again.');
    }
    setEditingChannel(null);
  };

  // User management handlers
  const loadUsersWithSubscriptions = async () => {
    try {
      const usersData = await adminApi.getUsersWithSubscriptions();
      setUsersWithSubs(usersData);
    } catch (error) {
      console.error('Failed to load users with subscriptions:', error);
    }
  };

  const handleOpenUserManagement = async () => {
    setShowUsersModal(true);
    await loadUsersWithSubscriptions();
  };

  const handleSaveUser = async () => {
    if (editingUser) {
      try {
        await adminApi.updateUser(editingUser.id, {
          name: editingUser.name
        });
        const updatedUsers = users.map(u =>
          u.id === editingUser.id ? editingUser : u
        );
        setUsers(updatedUsers);
        await loadUsersWithSubscriptions();
      } catch (error) {
        console.error('Failed to save user:', error);
        alert('Failed to save user. Please try again.');
      }
    }
    setEditingUser(null);
  };

  const handleToggleUserStatus = async (userId) => {
    try {
      const result = await adminApi.toggleUserStatus(userId);
      const updatedUsers = users.map(u =>
        u.id === userId ? { ...u, status: result.status } : u
      );
      setUsers(updatedUsers);
      await loadUsersWithSubscriptions();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      alert('Failed to toggle user status. Please try again.');
    }
  };

  const handleDeleteUserPermanently = async (userId) => {
    const userToDelete = usersWithSubs.find(u => u.id === userId);
    if (!userToDelete) return;
    
    const confirmed = window.confirm(
      `⚠️ PERMANENT DELETE\n\nAre you sure you want to permanently delete ${userToDelete.email}?\n\nThis will remove:\n- User account\n- All enrollments\n- All memberships\n- All chat messages\n\nThis action CANNOT be undone!`
    );
    
    if (!confirmed) return;
    
    // Double confirmation for safety
    const doubleConfirm = window.confirm(
      `Final confirmation: Delete ${userToDelete.email} permanently?`
    );
    
    if (!doubleConfirm) return;
    
    try {
      await adminApi.deleteUserPermanently(userId);
      await loadUsersWithSubscriptions();
      setSelectedUserForSubs(null);
      alert(`User ${userToDelete.email} has been permanently deleted.`);
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert(error.message || 'Failed to delete user. Please try again.');
    }
  };

  const handleResetUserPassword = async (userEmail) => {
    const newPassword = window.prompt(
      `Reset password for ${userEmail}\n\nEnter new password (min 6 characters):`
    );
    
    if (!newPassword) return;
    
    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters long.');
      return;
    }
    
    try {
      await adminApi.resetUserPassword(userEmail, newPassword);
      alert(`Password has been reset for ${userEmail}\n\nNew password: ${newPassword}\n\nPlease share this with the user securely.`);
    } catch (error) {
      console.error('Failed to reset password:', error);
      alert(error.message || 'Failed to reset password. Please try again.');
    }
  };

  const handleAssignMembership = async (userId, membershipId) => {
    try {
      await adminApi.assignMembership(userId, membershipId);
      await loadUsersWithSubscriptions();
      // Update the selected user to reflect the change immediately
      const updatedUsers = await adminApi.getUsersWithSubscriptions();
      const updatedUser = updatedUsers.find(u => u.id === userId);
      if (updatedUser) {
        setSelectedUserForSubs(updatedUser);
      }
      setShowAssignModal(null);
    } catch (error) {
      console.error('Failed to assign membership:', error);
      alert('Failed to assign membership. Please try again.');
    }
  };

  const handleRevokeMembership = async (userId) => {
    if (!window.confirm('Are you sure you want to revoke this membership?')) return;
    try {
      await adminApi.revokeMembership(userId);
      await loadUsersWithSubscriptions();
      // Update the selected user to reflect the change immediately
      const updatedUsers = await adminApi.getUsersWithSubscriptions();
      const updatedUser = updatedUsers.find(u => u.id === userId);
      if (updatedUser) {
        setSelectedUserForSubs(updatedUser);
      }
    } catch (error) {
      console.error('Failed to revoke membership:', error);
      alert('Failed to revoke membership. Please try again.');
    }
  };

  const handleAssignCourse = async (userId, courseId) => {
    try {
      await adminApi.assignCourse(userId, courseId);
      await loadUsersWithSubscriptions();
      // Update the selected user to reflect the change immediately
      const updatedUsers = await adminApi.getUsersWithSubscriptions();
      const updatedUser = updatedUsers.find(u => u.id === userId);
      if (updatedUser) {
        setSelectedUserForSubs(updatedUser);
      }
      setShowAssignModal(null);
    } catch (error) {
      console.error('Failed to assign course:', error);
      alert(error.message || 'Failed to assign course. Please try again.');
    }
  };

  const handleRevokeCourseAccess = async (userId, courseId) => {
    if (!window.confirm('Are you sure you want to revoke access to this course?')) return;
    try {
      await adminApi.revokeCourseAccess(userId, courseId);
      await loadUsersWithSubscriptions();
      // Update the selected user to reflect the change immediately
      const updatedUsers = await adminApi.getUsersWithSubscriptions();
      const updatedUser = updatedUsers.find(u => u.id === userId);
      if (updatedUser) {
        setSelectedUserForSubs(updatedUser);
      }
    } catch (error) {
      console.error('Failed to revoke course access:', error);
      alert('Failed to revoke course access. Please try again.');
    }
  };

  // Coupon handlers
  const handleCreateCoupon = async (couponData) => {
    try {
      const newCoupon = await couponApi.create(couponData);
      setCoupons(prev => [...prev, newCoupon]);
      return newCoupon;
    } catch (error) {
      console.error('Failed to create coupon:', error);
      throw error;
    }
  };

  const handleUpdateCoupon = async (couponId, couponData) => {
    try {
      const updatedCoupon = await couponApi.update(couponId, couponData);
      setCoupons(prev => prev.map(c => c.id === couponId ? updatedCoupon : c));
      return updatedCoupon;
    } catch (error) {
      console.error('Failed to update coupon:', error);
      throw error;
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    try {
      await couponApi.delete(couponId);
      setCoupons(prev => prev.filter(c => c.id !== couponId));
    } catch (error) {
      console.error('Failed to delete coupon:', error);
      alert('Failed to delete coupon. Please try again.');
    }
  };

  const handleToggleCoupon = async (couponId) => {
    try {
      const updatedCoupon = await couponApi.toggle(couponId);
      setCoupons(prev => prev.map(c => c.id === couponId ? updatedCoupon : c));
    } catch (error) {
      console.error('Failed to toggle coupon:', error);
      alert('Failed to toggle coupon status. Please try again.');
    }
  };

  // Failed payments handlers
  const loadFailedPayments = async () => {
    try {
      const payments = await failedPaymentsApi.getAll();
      setFailedPayments(payments);
      const countData = await failedPaymentsApi.getCount();
      setFailedPaymentsCount(countData.count || 0);
    } catch (error) {
      console.error('Failed to load failed payments:', error);
    }
  };

  const handleResolveFailedPayment = async (paymentId) => {
    try {
      await failedPaymentsApi.resolve(paymentId);
      setFailedPayments(prev => prev.map(p => 
        p.id === paymentId ? { ...p, status: 'resolved' } : p
      ));
      setFailedPaymentsCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to resolve payment:', error);
      alert('Failed to resolve payment. Please try again.');
    }
  };

  const handleDowngradeUserToFree = async (userId, failedPaymentId) => {
    if (!window.confirm('Downgrade this user to the Free plan now?\n\nThis takes away paid access and sends you a Telegram alert. If they pay later, the system will auto-restore their plan.')) {
      return;
    }
    try {
      const result = await failedPaymentsApi.downgradeToFree(userId, { failedPaymentId });
      if (result?.downgraded) {
        // Mark the failed_payment row as resolved in local state
        setFailedPayments(prev => prev.map(p =>
          p.id === failedPaymentId ? { ...p, status: 'auto_downgraded' } : p
        ));
        setFailedPaymentsCount(prev => Math.max(0, prev - 1));
        alert(`✓ Downgraded to Free (was on ${result.from_name || 'paid plan'})`);
      } else {
        alert(result?.message || 'Nothing to change — user already on Free.');
      }
    } catch (error) {
      console.error('Failed to downgrade user:', error);
      alert('Downgrade failed. Please try again.');
    }
  };

  const handleAddTestFailedPayment = async () => {
    try {
      await failedPaymentsApi.addTest();
      await loadFailedPayments();
    } catch (error) {
      console.error('Failed to add test payment:', error);
    }
  };

  // New members handlers
  const loadNewMembers = async () => {
    try {
      const members = await newMembersApi.getAll();
      setNewMembers(members);
      const countData = await newMembersApi.getCount();
      setNewMembersCount(countData.count || 0);
    } catch (error) {
      console.error('Failed to load new members:', error);
    }
  };

  const handleProcessNewMember = async (memberId) => {
    try {
      await newMembersApi.markProcessed(memberId);
      setNewMembers(prev => prev.map(m => 
        m.id === memberId ? { ...m, status: 'processed' } : m
      ));
      setNewMembersCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to process member:', error);
      alert('Failed to mark member as processed. Please try again.');
    }
  };

  const handleAddTestNewMember = async () => {
    try {
      await newMembersApi.addTest();
      await loadNewMembers();
    } catch (error) {
      console.error('Failed to add test member:', error);
    }
  };

  const handleSyncNewMemberAlerts = async () => {
    try {
      const result = await newMembersApi.syncAlerts();
      await loadNewMembers();
      // Refresh the count
      const countData = await newMembersApi.getCount();
      setNewMembersCount(countData.count || 0);
      alert(`Sync complete! Created ${result.alerts_created} new alerts. (${result.skipped_existing} already existed)`);
    } catch (error) {
      console.error('Failed to sync alerts:', error);
      alert('Failed to sync new member alerts. Please try again.');
    }
  };

  const handleRefreshNewMemberData = async () => {
    try {
      const result = await newMembersApi.refreshData();
      await loadNewMembers();
      alert(`Refresh complete! Updated ${result.records_updated} records with latest TradingView usernames.`);
    } catch (error) {
      console.error('Failed to refresh data:', error);
      alert('Failed to refresh new member data. Please try again.');
    }
  };

  const handleSyncUserFromStripe = async (email, userId = null) => {
    if (!email) {
      alert('No email provided');
      return;
    }
    
    if (!window.confirm(`Sync membership from Stripe for ${email}?`)) {
      return;
    }
    
    try {
      const result = await newMembersApi.syncUserFromStripe(email);
      if (result.success) {
        alert(`Success! ${email} now has: ${result.membership}\nStatus: ${result.status}\nExpires: ${result.expires}`);
        // Refresh user list
        await loadUsersWithSubscriptions();
      } else if (result.requires_manual_assignment && result.session_id) {
        // This is a guest checkout - offer to process it
        const confirmProcess = window.confirm(
          `${result.message}\n\nAmount paid: $${result.amount}\n\nWould you like to process this purchase now?\n(This will grant the course and any included bonuses)`
        );
        
        if (confirmProcess && userId) {
          try {
            const processResult = await newMembersApi.processGuestPurchase(userId, result.session_id);
            alert(`Success! ${processResult.message}`);
            await loadUsersWithSubscriptions();
          } catch (processError) {
            alert(`Failed to process purchase: ${processError.message}`);
          }
        } else if (confirmProcess && !userId) {
          alert('Cannot process automatically - user ID not available. Please use "Assign Course" manually.');
        }
      } else {
        alert(result.message || 'Sync failed');
      }
    } catch (error) {
      console.error('Failed to sync from Stripe:', error);
      alert(`Failed to sync: ${error.message}`);
    }
  };

  const handleCreateStripeSubscription = async (userId, userName) => {
    if (!userId) {
      alert('No user ID provided');
      return;
    }
    
    if (!window.confirm(`Create Stripe subscription for ${userName || userId}?\n\nThis will create a subscription that auto-renews after the trial period ends.`)) {
      return;
    }
    
    try {
      const result = await newMembersApi.createStripeSubscription(userId);
      alert(`Success!\n\nSubscription: ${result.subscription_id}\nMembership: ${result.membership}\nTrial days remaining: ${result.trial_days}\nWill auto-renew: Yes`);
      await loadUsersWithSubscriptions();
    } catch (error) {
      console.error('Failed to create subscription:', error);
      alert(`Failed to create subscription: ${error.message}`);
    }
  };

  // Load membership activity (both new members and failed payments)
  const loadMembershipActivity = async () => {
    await Promise.all([loadNewMembers(), loadFailedPayments()]);
  };

  // Profile handlers
  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a JPG, PNG, GIF, or WEBP image');
        return;
      }
      setIsUploading(true);
      setUploadProgress('Uploading profile picture...');
      try {
        console.log('Starting profile picture upload...');
        const uploadResult = await uploadApi.uploadImage(file);
        console.log('Upload successful, URL:', uploadResult.url);
        
        // Save the profile picture URL to database
        setUploadProgress('Saving profile picture...');
        const result = await updateUser({ profile_picture: uploadResult.url });
        
        if (result.success) {
          console.log('Profile picture saved successfully');
          setUploadProgress('Profile picture updated!');
          alert('Profile picture saved successfully!');
        } else {
          console.error('Failed to save profile picture:', result.error);
          alert('Failed to save profile picture: ' + (result.error || 'Unknown error'));
          setUploadProgress('');
        }
        
        setTimeout(() => {
          setUploadProgress('');
          setIsUploading(false);
        }, 2000);
      } catch (error) {
        console.error('Upload failed:', error);
        alert('Failed to upload profile picture: ' + error.message);
        setIsUploading(false);
        setUploadProgress('');
      }
    }
  };

  const handleSaveProfileName = async () => {
    if (profileName.trim()) {
      try {
        await updateUser({ name: profileName });
      } catch (error) {
        console.error('Failed to save name:', error);
      }
    }
  };

  const handleSaveTradingViewUsername = async () => {
    try {
      await updateUser({ tradingViewUsername });
    } catch (error) {
      console.error('Failed to save TradingView username:', error);
    }
  };

  // Chat handlers
  const handleSendMessage = async () => {
    if (newMessage.trim()) {
      const mentionRegex = /@(\w+)/g;
      const mentionMatches = [...newMessage.matchAll(mentionRegex)];
      const mentionedNames = mentionMatches.map(m => m[1].toLowerCase());
      const mentionedUserIds = onlineUsers
        .filter(u => mentionedNames.includes(u.name?.toLowerCase()))
        .map(u => u.id);
      chatWebSocket.sendMessage(newMessage, {
        replyTo: replyingTo,
        mentions: mentionedUserIds
      });
      setNewMessage('');
      setReplyingTo(null);
      setShowMentionSuggestions(false);
    }
  };

  const handleTyping = (e) => {
    setNewMessage(e.target.value);
    chatWebSocket.startTyping();
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = e.target.value.substring(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    if (mentionMatch) {
      setMentionFilter(mentionMatch[1].toLowerCase());
      setShowMentionSuggestions(true);
    } else {
      setShowMentionSuggestions(false);
    }
  };

  const insertMention = (userName) => {
    const cursorPos = chatInputRef.current?.selectionStart || newMessage.length;
    const textBeforeCursor = newMessage.substring(0, cursorPos);
    const textAfterCursor = newMessage.substring(cursorPos);
    const mentionMatch = textBeforeCursor.match(/@\w*$/);
    if (mentionMatch) {
      const newText = textBeforeCursor.replace(/@\w*$/, `@${userName} `) + textAfterCursor;
      setNewMessage(newText);
    }
    setShowMentionSuggestions(false);
    chatInputRef.current?.focus();
  };

  const handleReaction = (messageId, emoji) => {
    chatWebSocket.addReaction(messageId, emoji);
    setShowEmojiPicker(null);
  };

  // Toggle sound and save preference to backend
  const toggleSound = async (enabled) => {
    setSoundEnabled(enabled);
    try {
      const token = localStorage.getItem('hyperdriveToken');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ chat_sound: enabled })
      });
    } catch (error) {
      console.log('Failed to save sound preference:', error);
    }
  };

  const handleEditMessage = (message) => {
    setEditingMessage(message);
    setNewMessage(message.text);
    chatInputRef.current?.focus();
  };

  const handleSaveEdit = () => {
    if (editingMessage && newMessage.trim()) {
      chatWebSocket.editMessage(editingMessage.id, newMessage);
      setEditingMessage(null);
      setNewMessage('');
    }
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setNewMessage('');
  };

  const handleDeleteMessage = (messageId) => {
    const confirmMsg = user?.role === 'admin'
      ? 'Are you sure you want to delete this message? (Admin)'
      : 'Are you sure you want to delete this message?';
    if (window.confirm(confirmMsg)) {
      chatWebSocket.deleteMessage(messageId);
    }
  };

  const handleMuteUser = (targetUserId, targetUserName) => {
    const duration = prompt(`Mute ${targetUserName} for how many minutes?`, '60');
    if (duration) {
      const minutes = parseInt(duration, 10);
      if (!isNaN(minutes) && minutes > 0) {
        chatWebSocket.muteUser(targetUserId, minutes);
        alert(`${targetUserName} has been muted for ${minutes} minutes`);
      }
    }
  };

  const handleUnmuteUser = (targetUserId, targetUserName) => {
    if (window.confirm(`Unmute ${targetUserName}?`)) {
      chatWebSocket.unmuteUser(targetUserId);
      alert(`${targetUserName} has been unmuted`);
    }
  };

  const handleSearchMessages = async () => {
    if (searchQuery.trim()) {
      try {
        const results = await chatApi.searchMessages(searchQuery, activeChannel);
        setSearchResults(results);
        setShowSearchResults(true);
      } catch (error) {
        console.error('Search failed:', error);
      }
    }
  };

  const handleChatImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setChatImageUploading(true);
    try {
      const result = await uploadApi.uploadImage(file);
      if (result.url) {
        chatWebSocket.sendMessage('', { imageUrl: result.url });
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setChatImageUploading(false);
      if (chatImageInputRef.current) {
        chatImageInputRef.current.value = '';
      }
    }
  };

  const value = {
    // Auth
    user,
    logout,
    updateUser,

    // Helpers
    formatDate,
    formatMessageTime,
    getNextSessionTime,
    getTodayFormatted,

    // UI State
    activePage,
    setActivePage,
    countdown,
    nextSessionDate,
    cancelledDates,
    isUploading,
    setIsUploading,
    uploadProgress,
    setUploadProgress,

    // Data
    trades,
    setTrades,
    refreshTrades,
    customPairs,
    setCustomPairs,
    courses,
    setCourses,
    enrollments,
    setEnrollments,
    memberships,
    setMemberships,
    channels,
    setChannels,
    users,
    setUsers,
    usersWithSubs,
    setUsersWithSubs,
    streamData,
    setStreamData,

    // Chat State
    activeChannel,
    setActiveChannel,
    channelMessages,
    setChannelMessages,
    newMessage,
    setNewMessage,
    onlineUsers,
    setOnlineUsers,
    typingUsers,
    setTypingUsers,
    unreadCounts,
    setUnreadCounts,
    soundEnabled,
    toggleSound,
    showEmojiPicker,
    setShowEmojiPicker,
    replyingTo,
    setReplyingTo,
    editingMessage,
    setEditingMessage,
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    showSearchResults,
    setShowSearchResults,
    chatImageUploading,
    setChatImageUploading,
    showMentionSuggestions,
    setShowMentionSuggestions,
    mentionFilter,
    setMentionFilter,

    // Modals
    showTradeModal,
    setShowTradeModal,
    showAddPairModal,
    setShowAddPairModal,
    showMembershipModal,
    setShowMembershipModal,
    showChannelsModal,
    setShowChannelsModal,
    showUsersModal,
    setShowUsersModal,
    showCoursesModal,
    setShowCoursesModal,
    showCourseBuilder,
    setShowCourseBuilder,
    showCourseViewer,
    setShowCourseViewer,
    showAssignModal,
    setShowAssignModal,

    // Edit State
    editingTrade,
    setEditingTrade,
    editingCourse,
    setEditingCourse,
    editingMembership,
    setEditingMembership,
    editingChannel,
    setEditingChannel,
    editingUser,
    setEditingUser,
    viewingCourseId,
    setViewingCourseId,
    selectedUserForSubs,
    setSelectedUserForSubs,

    // Form State
    profileName,
    setProfileName,
    tradingViewUsername,
    setTradingViewUsername,
    newPairName,
    setNewPairName,
    enrollingCourseId,
    setEnrollingCourseId,
    upgradingMembershipId,
    setUpgradingMembershipId,
    redirectingToStripe,
    setRedirectingToStripe,
    tradeForm,
    setTradeForm,

    // Refs
    fileInputRef,
    chatInputRef,
    chatMessagesRef,
    chatImageInputRef,

    // Constants
    defaultPairs,
    commonEmojis,

    // Computed
    stats,
    equityCurveData,

    // Trade handlers
    handleTradeFormChange,
    handleAddTrade,
    handleEditTrade,
    handleDeleteTrade,
    handleAddCustomPair,

    // Enrollment handlers
    handleEnrollCourse,
    isEnrolled,
    openCourseViewer,

    // Stream handlers
    handleGoLive,
    handleCancelSession,
    handleUncancelSession,

    // Membership handlers
    handleSaveMembership,
    handleAddNewMembership,
    handleDeleteMembership,
    handleUpgradeMembership,
    handleOpenBillingPortal,
    
    // Checkout modal
    checkoutModalMembership,
    openCheckoutModal,
    closeCheckoutModal,
    handleCheckoutWithCoupon,

    // Channel handlers
    handleSaveChannel,
    handleAddNewChannel,
    handleDeleteChannel,

    // User management handlers
    loadUsersWithSubscriptions,
    handleOpenUserManagement,
    handleSaveUser,
    handleToggleUserStatus,
    handleDeleteUserPermanently,
    handleResetUserPassword,
    handleAssignMembership,
    handleRevokeMembership,
    handleAssignCourse,
    handleRevokeCourseAccess,

    // Coupon handlers
    coupons,
    handleCreateCoupon,
    handleUpdateCoupon,
    handleDeleteCoupon,
    handleToggleCoupon,

    // Failed payments
    failedPayments,
    failedPaymentsCount,
    loadFailedPayments,
    handleResolveFailedPayment,
    handleAddTestFailedPayment,
    handleDowngradeUserToFree,

    // New members
    newMembers,
    newMembersCount,
    loadNewMembers,
    handleProcessNewMember,
    handleAddTestNewMember,
    handleSyncNewMemberAlerts,
    handleRefreshNewMemberData,
    handleSyncUserFromStripe,
    handleCreateStripeSubscription,
    loadMembershipActivity,

    // Profile handlers
    handleProfilePictureUpload,
    handleSaveProfileName,
    handleSaveTradingViewUsername,

    // Chat handlers
    handleSendMessage,
    handleTyping,
    insertMention,
    handleReaction,
    handleEditMessage,
    handleSaveEdit,
    handleCancelEdit,
    handleDeleteMessage,
    handleMuteUser,
    handleUnmuteUser,
    handleSearchMessages,
    handleChatImageUpload
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardContext;